import {
    EnvironmentInjector,
    inject,
    Injectable,
    Injector,
    Type,
    DestroyRef,
    effect,
    signal,
    computed,
} from '@angular/core';
import { Observable, EMPTY, firstValueFrom, from, shareReplay } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OverlayRef } from './overlay.ref';
import type { OverlayDirective } from './overlay.directive';

@Injectable()
export class OverlayService {
    readonly #envInjector = inject(EnvironmentInjector);
    readonly #destroyRef = inject(DestroyRef);

    readonly #hostSig = signal<OverlayDirective | null>(null);
    readonly ready = computed<boolean>(() => this.#hostSig() !== null);

    readonly ready$ = toObservable(this.ready).pipe(
        filter(Boolean),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    registerHost(host: OverlayDirective) {
        this.#hostSig.set(host);

        effect(
            () => {
                const current = this.#hostSig();
                if (!current) return;
            },
            { injector: this.#envInjector, manualCleanup: true },
        );
    }

    unregisterHost() {
        this.#hostSig.set(null);
    }

    close() {
        this.#hostSig()?.viewContainerRef?.clear?.();
    }

    open$<T, R = any>(
        component: Type<T>,
        options?: { inputs?: Record<string, unknown> },
    ): Observable<R> {
        return from(this.#waitForHost()).pipe(
            switchMap(() => {
                const host = this.#hostSig();
                if (!host) return EMPTY;

                host.viewContainerRef.clear();

                const modalRef = new OverlayRef<R>();
                const injector = Injector.create({
                    providers: [{ provide: OverlayRef, useValue: modalRef }],
                    parent: this.#envInjector,
                });

                const compRef = host.viewContainerRef.createComponent(
                    component,
                    { injector },
                );

                if (options?.inputs) {
                    for (const [key, value] of Object.entries(options.inputs)) {
                        compRef.setInput(key as never, value as never);
                    }
                }

                modalRef.closed
                    .pipe(takeUntilDestroyed(this.#destroyRef))
                    .subscribe(() => {
                        compRef.destroy();
                    });

                return modalRef.closed;
            }),
        );
    }

    async #waitForHost(): Promise<void> {
        if (this.#hostSig()) return;
        await firstValueFrom(this.ready$.pipe(take(1)));
    }
}
